import React from 'react';
import html2pdf from 'html2pdf.js';
import { useTranslation } from "../contexts/TranslationContext";

const pdfLabels = {
  pt: {
    downloadPdf: "Baixar PDF",
    contact: "Contato",
    skills: "Habilidades",
    languages: "Idiomas",
    references: "Referências",
    experiences: "Experiência",
    education: "Educação",
    phone: "Telefone",
    email: "Email",
    address: "Endereço",
    website: "Website",
    present: "Presente",
  },
  en: {
    downloadPdf: "Download PDF",
    contact: "Contact",
    skills: "Skills",
    languages: "Languages",
    references: "References",
    experiences: "Experience",
    education: "Education",
    phone: "Phone",
    email: "Email",
    address: "Address",
    website: "Website",
    present: "Present",
  },
};

export default function ResumePdfContent({ data, isOpen, onClose }) {
  const { t } = useTranslation();

  if (!isOpen || !data) return null;

const {
  nomeCurriculo,
  name,
  phone,
  email,
  address,
  website,
  linkedin,
  github,
  skills = [],
  languages = [],
  references = [],
  summary,
  desiredRole,
  education = [],
  cvLanguage,
  useAppLanguage,
} = data;

  // Original: segue idioma do app. Tunado: detecta pelo conteúdo.
  let detectedLang = null;
  if (!useAppLanguage) {
    const rawExperiences = data.experiences || [];
    const allText = [summary || "", desiredRole || "", ...rawExperiences.map(e => e.descricao || "")].join(" ");
    const ptWords = allText.length > 20 ? allText.match(/\b(experiência|desenvolvimento|responsável|aplicações|desempenho|equipe|gerenciamento|implementação|otimização|escaláveis|manutenção|criação)\b/gi) : null;
    detectedLang = allText.length > 20 ? ((ptWords && ptWords.length >= 2) ? "pt" : "en") : null;
  }
  const labels = detectedLang ? pdfLabels[detectedLang] : null;
  const label = (key) => labels ? labels[key] : t(`resumePdf.${key}`);

  const { experiences = [] } = data;

  const formatExperiencePeriod = (exp) => {
    if (!exp.startDate) return "";
    const start = new Date(exp.startDate);
    const startMonth = start.toLocaleString('default', { month: 'short' });
    const startYear = start.getFullYear();

    if (exp.isCurrentJob) {
      return `${startMonth} ${startYear} - ${label('present')}`;
    }

    if (exp.endDate) {
      const end = new Date(exp.endDate);
      const endMonth = end.toLocaleString('default', { month: 'short' });
      const endYear = end.getFullYear();
      return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    }

    return `${startMonth} ${startYear}`;
  };

  const formattedLinkedin = linkedin?.startsWith("http")
    ? linkedin
    : `https://${linkedin}`;

  const downloadPdf = () => {
    const element = document.getElementById("pdf-content");
    html2pdf()
      .from(element)
      .set({
        margin: [0, 10, 10, 10],
        filename: `${nomeCurriculo || "curriculo"}.pdf`,
        html2canvas: { scale: 2, scrollY: 0 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        enableLinks: true,
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      })
      .save();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden">
      <div className="relative bg-white max-w-[850px] w-full rounded-xl shadow-xl h-screen overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-300">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 text-lg font-bold"
          >
            ✕
          </button>
          <button
            onClick={downloadPdf}
            className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700 transition"
          >
            {label("downloadPdf")}
          </button>
        </div>

        <div
          id="pdf-content"
          className="text-[13px] text-gray-900 font-inter flex flex-col items-center gap-4 py-1"
        >
          {/* Página A4 simulada */}
          <div className="w-[794px] min-h-[1122px] px-[50px] py-[5px] bg-white box-border shadow border border-gray-200">
            <h1 className="text-2xl font-bold mb-1">{name}</h1>
            {desiredRole && <h2 className="text-md text-gray-600 mb-4">{desiredRole}</h2>}
            {summary && <p className="mb-6 text-sm">{summary}</p>}

            <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
              {label("contact")}
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 mb-6">
              {phone && <p>{label("phone")}: {phone}</p>}
              {email && <p>{label("email")}: {email}</p>}
              {address && <p>{label("address")}: {address}</p>}
              {website && <p>{label("website")}: {website}</p>}
              {github && <p>GitHub: {github}</p>}
              {linkedin && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-700 break-all">
                    <span className="font-semibold">LinkedIn:</span>{" "}
                    <a
                      href={formattedLinkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 no-underline hover:underline"
                    >
                      {formattedLinkedin}
                    </a>
                  </p>
                </div>
              )}
            </div>

            {skills.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                  {label("skills")}
                </h2>
                <p className="text-sm text-gray-700 mb-6">{skills.join(", ")}</p>
              </>
            )}

            {languages.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                  {label("languages")}
                </h2>
                <ul className="list-disc ml-5 text-sm text-gray-700 mb-6">
                  {languages.map((lang, i) => (
                    <li key={i}>{lang}</li>
                  ))}
                </ul>
              </>
            )}

            {references.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                  {label("references")}
                </h2>
                <ul className="list-disc ml-5 text-sm text-gray-700 mb-6">
                  {references.map((ref, i) => (
                    <li key={i}>
                      {ref.name}
                      {ref.contact ? `, ${ref.contact}` : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {experiences.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                  {label("experiences")}
                </h2>
                {experiences.map((exp, idx) => {
                  const period = formatExperiencePeriod(exp);

                  return (
                    <div key={idx} className="mb-4 break-inside-avoid">
                      <p className="font-semibold text-sm">
                        {exp.cargo} @ {exp.empresa}
                      </p>
                      {period && (
                        <p className="text-xs text-gray-500 italic">{period}</p>
                      )}
                      <p className="text-xs text-gray-600 whitespace-pre-line">
                        {exp.descricao}
                      </p>
                    </div>
                  );
                })}
              </>
            )}

            {education.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                  {label("education")}
                </h2>
                {education.map((edu, idx) => (
                  <div key={idx} className="mb-3 break-inside-avoid">
                    <p className="font-semibold text-sm">
                      {edu.course} @ {edu.university}
                    </p>
                    <p className="text-xs text-gray-600">
                      {edu.startDate ? new Date(edu.startDate).getFullYear() : ""}
                      {edu.startDate && edu.endDate ? " - " : ""}
                      {edu.endDate ? new Date(edu.endDate).getFullYear() : ""}
                    </p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
